import { ToastT } from "sonner";
import { z } from "zod";

// #region Fetch Response and JWT Types
export type FetchResponse = {
  status: number;
  data: unknown;
  error?: string;
};

export type JWT = {
  refreshToken: string;
};

export type GenericProviderState<T> = {
  data: T | null;
  loading: boolean;
  error: "unauthorized" | "network_error" | (string & {}) | null;
};

// #endregion Fetch Response and JWT Types

// #region Tag Types
export const TagSchema = z.object({
  id: z.number(),
  name: z.string(),
  color: z.string(),
});
export type Tag = z.infer<typeof TagSchema>;
// #endregion Tag Types

// #region Basic Task Types
export const BasicTaskSchema = z.object({
  id: z.number(),
  title: z.string(),
  due_date: z.string().nullable(),
  assignee_id: z.number(),
  assignee_name: z.string(),
  tags: z.array(TagSchema),
  status: z.enum(["complete", "incomplete"]),
  parent_id: z.number().nullable(),
});
export type BasicTask = z.infer<typeof BasicTaskSchema>;
// #endregion Basic Task Types

// #region Collaborator Types
export const CollaboratorSchema = z.object({
  id: z.number(),
  full_name: z.string(),
  email: z.string(),
});
export type Collaborator = z.infer<typeof CollaboratorSchema>;
// #endregion Collaborator Types

// #region New Task Types
export const NewTaskTagSchema = z.object({
  category_id: z.number(),
  tag: z.string(),
  color: z.string(),
});
export type NewTaskTag = z.infer<typeof NewTaskTagSchema>;

export const TagCategorySchema = z.object({
  id: z.number(),
  category: z.string(),
  tags: z.array(TagSchema),
});
export type TagCategory = z.infer<typeof TagCategorySchema>;
// #endregion New Task Types

// #region Child Task Types
export const ChildTaskSchema = z.object({
  id: z.number(),
  title: z.string(),
  due_date: z.string().nullable(),
  assignee_name: z.string(),
  assignee_id: z.number(),
  status: z.string(),
  tags: z.array(TagSchema),
});
export type ChildTask = z.infer<typeof ChildTaskSchema>;
// #endregion Child Task Types

// #region Audit Log Types
const SingleAuditLogCreateSchema = z.object({
  type: z.literal("create"),
  full_name: z.string(),
  timestamp: z.string(),
});
export type SingleAuditLogCreate = z.infer<typeof SingleAuditLogCreateSchema>;

const SingleAuditLogChangeSchema = z.object({
  field: z.enum([
    "parent_id",
    "title",
    "description",
    "assigned_to",
    "collaborators",
    "status",
    "due_date",
    "tags",
  ]),
  old_value: z.string(),
  new_value: z.string(),
});
export type SingleAuditLogChange = z.infer<typeof SingleAuditLogChangeSchema>;

const SingleAuditLogUpdateSchema = z.object({
  type: z.literal("update"),
  changes: z.array(SingleAuditLogChangeSchema),
  full_name: z.string(),
  timestamp: z.string(),
});
export type SingleAuditLogUpdate = z.infer<typeof SingleAuditLogUpdateSchema>;

const SingleAuditLogCopy = z.object({
  type: z.literal("copy"),
  full_name: z.string(),
  timestamp: z.string(),
});

export const SingleAuditLogSchema = z.union([
  SingleAuditLogCreateSchema,
  SingleAuditLogUpdateSchema,
  SingleAuditLogCopy,
]);
export type SingleAuditLog = z.infer<typeof SingleAuditLogSchema>;

// #endregion Audit Log Types

// #region Full Task Types
export const FullTaskSchema = BasicTaskSchema.extend({
  status: z.string(),
  description: z.string().nullable(),
  // audit_logs: z.array(SingleAuditLogSchema),
  collaborators: z.array(CollaboratorSchema),
  children: z.array(ChildTaskSchema),
});
export type FullTask = z.infer<typeof FullTaskSchema>;
// #endregion Full Task Types

// #region New Task Types
export const NewTaskSchema = z.object({
  assigned_to: z.number().optional(),
  title: z.string().optional(),
  due_date: z.string().nullable().optional(),
  input: z.string().nullable().optional(),
  collaborators: z.array(z.number()).nullable().optional(),
  tags: z.array(NewTaskTagSchema).nullable().optional(),
  parent_id: z.number().nullable().optional(),
});
export type NewTask = z.infer<typeof NewTaskSchema>;

export const EditedTaskSchema = NewTaskSchema.extend({
  id: z.number(),
  status: z.string().nullable().optional(),
});
export type EditedTask = z.infer<typeof EditedTaskSchema>;
// #endregion New Task Types

// #region Task Params Types
export type FetchTaskParams = {
  taskID: number;
  signal?: AbortSignal;
  editing?: boolean;
};
// #endregion Task Params Types

// #region User Types
export const ShortAppUserSchema = z.object({
  id: z.number(),
  first_name: z.string(),
  surname: z.string(),
  email: z.string(),
  timezone: z.string(),
  profile_picture: z.string(),
  role_name: z.string(),
});
export type ShortAppUser = z.infer<typeof ShortAppUserSchema>;
// #endregion User Types

// #region User Timer Types
export const UserTimerEventSchema = z.object({
  position: z.number(),
  start: z.string(),
  end: z.string().nullable().optional(),
});
export type UserTimerEvent = z.infer<typeof UserTimerEventSchema>;

export const NewUserTimerEventSchema = UserTimerEventSchema.omit({
  position: true,
});

export type NewUserTimerEvent = z.infer<typeof NewUserTimerEventSchema>;

export const NewUserTimerSchema = z.object({
  name: z.string(),
  task_id: z.number().nullable(),
  events: z.array(NewUserTimerEventSchema),
});
export type NewUserTimer = z.infer<typeof NewUserTimerSchema>;

export const UserTimerSchema = z.object({
  id: z.number(),
  name: z.string(),
  task_id: z.number().nullable(),
  user_id: z.number(),
  events: z.array(UserTimerEventSchema),
});
export type UserTimer = z.infer<typeof UserTimerSchema>;

// TODO: revist this type
export const SubmitUserTimerUpdateSchema = z.object({
  id: z.number().nullable().optional(),
  name: z.string().nullable().optional(),
  task_id: z.number().nullable().optional(),
  events: z.array(UserTimerEventSchema).nullable().optional(),
  user_id: z.number().nullable().optional(),
});
export type SubmitUserTimerUpdate = z.infer<typeof SubmitUserTimerUpdateSchema>;

export const FullTimerSchema = UserTimerSchema.extend({
  task_name: z.string().nullable(),
  tags: z.array(z.number()).nullable(),
  totalTime: z.number().nullable(),
});
export type FullTimer = z.infer<typeof FullTimerSchema>;
// #endregion User Timer Types

// #region Favorite Searches
export const FavoriteSearchSchema = z.object({
  id: z.number(),
  name: z.string(),
  search: z.string(),
  favourite_order: z.number(),
});
export type FavoriteSearch = z.infer<typeof FavoriteSearchSchema>;

// #endregion Favorite Searches

// #region Search: Due Date Types
export const WithinDateSchema = z.object({
  timePeriods: z.enum(["Days", "Weeks", "Months"]).nullable(),
  timeUnit: z.number().nullable(),
});
export type WithinDate = z.infer<typeof WithinDateSchema>;

export const DateRangeSearchSchema = z.object({
  fromDate: z.string().nullable(),
  toDate: z.string().nullable(),
});
export type DateRangeSearch = z.infer<typeof DateRangeSearchSchema>;

export const DateTypeSchema = z
  .enum([
    "Any Date",
    "Specific Date",
    "Yesterday",
    "Today",
    "Tomorrow",
    "Within the last",
    "Within the next",
    "Date Range",
    "Overdue",
  ])
  .nullable();
export const DateTypeDataSchema = z.object({
  type: DateTypeSchema,
  data: z.union([WithinDateSchema, DateRangeSearchSchema, z.date().nullable()]),
});
export type DateType = z.infer<typeof DateTypeSchema>;
export type DateTypeData = z.infer<typeof DateTypeDataSchema>;

// #endregion Search: Due Date Types

// #region Add Filter Types

export const AddFilterDateTypeSchema = z
  .enum(["Last Modified", "Created Date", "Completed Date"])
  .nullable();
export type AddFilterDateType = z.infer<typeof AddFilterDateTypeSchema>;

export const AddFilterPeopleTypeSchema = z
  .enum(["Assigned by", "Created By", "Updated By", "Has Timer"])
  .nullable();
export type AddFilterPeopleType = z.infer<typeof AddFilterPeopleTypeSchema>;

export const AddFilterOptionsSchema = z.object({
  date: z.array(AddFilterDateTypeSchema).nullable(),
  people: z.array(AddFilterPeopleTypeSchema).nullable(),
});
export type AddFilterOptions = z.infer<typeof AddFilterOptionsSchema>;

// #endregion Add Filter Types

// #region Persist Search Params

export const searchParamStatusSchema = z.enum([
  "complete",
  "incomplete",
  "all",
]);
export type SearchParamStatus = z.infer<typeof searchParamStatusSchema>;

export const SEARCH_TAG_TYPES = ["exact", "partial", "any"] as const;
export const SEARCH_COLLABORATOR_TYPES = ["exact", "partial", "any"] as const;

export const SEARCH_INVERSE_FILTERS = [
  "title",
  "status",
  "collaborators",
  "assignedTo",
  "dueDate",
  "tag",
  "createdBy",
  "updatedBy",
  "hasUsersTimer",
  "assignedBy",
  "createdDate",
  "completedDate",
  "modifiedDate",
] as const;

export const SearchParamInverseFilterSchema = z.enum(SEARCH_INVERSE_FILTERS);

export type SearchParamInverseFilter = z.infer<
  typeof SearchParamInverseFilterSchema
>;

export const SearchParamsObjSchema = z.object({
  assignedTo: z.array(z.number().nullable()),
  collaboratorInput: z.array(CollaboratorSchema),
  collaboratorSearchType: z.enum(SEARCH_COLLABORATOR_TYPES),
  title: z.string().nullable(),
  status: searchParamStatusSchema.nullable(),
  dueDateType: z.string().nullable(),
  dueDateData: z
    .union([
      WithinDateSchema,
      DateRangeSearchSchema,
      z.date().nullable(),
      z.string().nullable(),
    ])
    .nullable(),
  tagType: z.enum(SEARCH_TAG_TYPES),
  tagData: z.array(NewTaskTagSchema),
  createdDateType: z.string().nullable(),
  createdDateData: z
    .union([
      WithinDateSchema,
      DateRangeSearchSchema,
      z.date().nullable(),
      z.string().nullable(),
    ])
    .nullable(),
  completedDateType: z.string().nullable(),
  completedDateData: z
    .union([
      WithinDateSchema,
      DateRangeSearchSchema,
      z.date().nullable(),
      z.string().nullable(),
    ])
    .nullable(),
  modifiedDateType: z.string().nullable(),
  modifiedDateData: z
    .union([
      WithinDateSchema,
      DateRangeSearchSchema,
      z.date().nullable(),
      z.string().nullable(),
    ])
    .nullable(),
  assignedBy: z.array(z.number().nullable()),
  createdBy: z.array(z.number().nullable()),
  updatedBy: z.array(z.number().nullable()),
  hasTimer: z.array(z.number().nullable()),
  inverseFilters: z.array(SearchParamInverseFilterSchema),
});
export type SearchParamsObj = z.infer<typeof SearchParamsObjSchema>;

// #endregion Persist Search Params

// #region OrganizedTimersEvents
export type OrganizedTimersEvent = {
  timer: {
    id: FullTimer["id"];
    name: FullTimer["name"];
    task_id: FullTimer["task_id"];
    user_id: FullTimer["user_id"];
    task_name: FullTimer["task_name"];
    tags: FullTimer["tags"];
    totalTime: FullTimer["totalTime"];
  };
  position: FullTimer["events"][number]["position"];
  event_start: FullTimer["events"][number]["start"];
  event_end: FullTimer["events"][number]["end"];
};

export type OrganizedTimersEvents = {
  today: OrganizedTimersEvent[];
  thisWeek: OrganizedTimersEvent[];
  older: OrganizedTimersEventsByMonth[];
  noEvents: OrganizedTimersEvent[];
};

export type OrganizedTimersEventsByMonth = {
  month: string;
  timers: OrganizedTimersEvent[];
};
// #endregion OrganizedTimersEvents

// #region Chat Types
export const ChatMessageSchema = z.object({
  message: z.string(),
  sender: z.enum(["user", "bot"]),
  timestamp: z.number().refine(
    (val) => {
      // Check if valid timestamp
      return new Date(val).getTime() > 0;
    },
    {
      message: "Invalid date timestamp",
    }
  ),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;
// #endregion Chat Types

// #region Search Task Types

export const SearchTimerSchema = z.object({
  id: z.number(),
  name: z.string(),
  username: z.string(),
  task_id: z.number(),
  events: z.array(UserTimerEventSchema),
});
export type SearchTimer = z.infer<typeof SearchTimerSchema>;

export const SearchTaskBasicSchema = BasicTaskSchema.extend({
  collaborators: z.array(z.number()),
});
export type SearchTaskBasic = z.infer<typeof SearchTaskBasicSchema>;

export const SearchTaskAdvancedSchema = BasicTaskSchema.extend({
  collaborators: z.array(z.number()),
  history: z.array(SingleAuditLogSchema),
  timers: z.array(SearchTimerSchema),
  created_at: z.string(),
  updated_at: z.string(),
});
export type SearchTaskAdvanced = z.infer<typeof SearchTaskAdvancedSchema>;

// Search task with timestamp (Basic)
export const SearchTaskBasicResponseSchema = z.object({
  tasks: z.array(SearchTaskBasicSchema),
  timestamp: z.number(),
});
export type SearchTaskBasicResponse = z.infer<
  typeof SearchTaskBasicResponseSchema
>;

// Search task with timestamp (Advanced)
export const SearchTaskAdvancedResponseSchema = z.object({
  tasks: z.array(SearchTaskAdvancedSchema),
  timestamp: z.number(),
});
export type SearchTaskAdvancedResponse = z.infer<
  typeof SearchTaskAdvancedResponseSchema
>;

// Legacy type for now, can be removed later if SearchTaskAdvanced is used everywhere
export const SearchTaskSchema = SearchTaskAdvancedSchema;
export type SearchTask = z.infer<typeof SearchTaskSchema>;
export const SearchTaskWithTimestampSchema = SearchTaskAdvancedResponseSchema;
export type SearchTaskWithTimestamp = z.infer<
  typeof SearchTaskWithTimestampSchema
>;

// #endregion Search Task Types

// #region Notification Types

export const NotificationActivitySchema = z.object({
  field: z.enum([
    "parent_id",
    "title",
    "description",
    "assigned_to",
    "collaborators",
    "status",
    "due_date",
    "tags",
  ]),
  value: z.string(),
});

export type UserNotificationActivity = z.infer<
  typeof NotificationActivitySchema
>;

export const UserNotificationSchema = z.object({
  id: z.number(),
  notification_date: z.string(),
  activity: z.array(NotificationActivitySchema),
  status: z.enum(["Complete", "Incomplete"]),
  task: z.number(),
  sender: z.number(),
});

export type UserNotification = z.infer<typeof UserNotificationSchema>;

// #endregion Notification Types

// #region TagMetrics
export const TagMetricsSchema = z.object({
  tag_id: z.number(),
  current_tasks_count: z.number(),
  created_tasks_count: z.object({
    "7_days": z.number(),
    "30_days": z.number(),
    "90_days": z.number(),
  }),
  completed_tasks_count: z.number(),
});

export type TagMetrics = z.infer<typeof TagMetricsSchema>;
// #endregion TagMetrics

// #region ToastOptions
export type ToastOptions = {
  position?: ToastT["position"];
  actionLabel?: string;
  callback?: () => void;
};

// #endregion ToastOptions

export type AllURLSearchFilterValues = {
  title: string | null;
  status: SearchParamsObj["status"] | null;
  collaborators: number[] | null;
  collaboratorSearchType: SearchParamsObj["collaboratorSearchType"] | null;
  assignedTo: number[] | null;
  dueDateType: DateType | null;
  dueDateData: WithinDate | DateRangeSearch | Date | null;
  tagType: SearchParamsObj["tagType"] | null;
  tagData: string[] | null;
  createdBy: number[] | null;
  updatedBy: number[] | null;
  hasUsersTimer: number[] | null;
  assignedBy: number[] | null;
  createdDateType: DateType | null;
  createdDateData: WithinDate | DateRangeSearch | Date | null;
  completedDateType: DateType | null;
  completedDateData: WithinDate | DateRangeSearch | Date | null;
  modifiedDateType: DateType | null;
  modifiedDateData: WithinDate | DateRangeSearch | Date | null;
  inverseFilters: SearchParamsObj["inverseFilters"] | null;
};
